import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../hooks/useStore';
import { useCreateCustomBenefit } from '../hooks/useCreateCustomBenefit';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { Typography, Box, Grid, Divider, Paper, IconButton } from '@mui/material';
import { CurrencyInputDialog, ConfirmationDialog } from '../../../../lib/components';
import { HelpIcon, LockIcon, SaveIcon } from '../../../../lib/icons';
import Util from '../../../../misc/util';
import { ApiError, BenefitEnum } from '@pxtra-client-lib-legacy/gen';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { theme } from '../../../../lib/theme';
import { AutomaticAssignmentDialog, LoadingDialog } from '../../../benefits/customBenefitDialogs';
import { Button } from '../../../../lib/components/atomic/button';
import { CustomBenefitAssignmentTable } from '../../../../lib/components/tables/customBenefitAssignment';
import {
  useCreateCustomBenefitAssignments,
  useDeleteCustomBenefitAssignments,
  useListCustomBenefitAssignments,
  customBenefitAssignmentKeys
} from '../../../../queries/benefit/customBenefitAssignment';
import { useQueryClient } from '@tanstack/react-query';

export const EmployeeSelection = () => {
  const [nextMonth] = useState(() => new Date(new Date().setMonth(new Date().getMonth() + 1, 1)));

  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companyStore } = useStore();
  const { t } = useTranslation();
  const { newCustomBenefit, patchCustomBenefit: patch } = useCreateCustomBenefit();
  const [showAutomaticAssignmentDialog, setShowAutomaticAssignmentDialog] = useState(false);
  const [isValidatingConstraints, setIsValidatingConstraints] = useState<boolean>(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [showLeavingDialog, setShowLeavingDialog] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [showInfoDrawer, setShowInfoDrawer] = useState(false);
  const [conflictFree, setConflictFree] = useState(false);
  const [correctAssignments, setCorrectAssignments] = useState(false);

  const benefitId = newCustomBenefit.editingBenefitId || '';
  const assignmentsStartDate = newCustomBenefit.employeeAssignmentsStart ?? nextMonth;
  const assignmentsStart = Util.getAccountingPeriod(assignmentsStartDate);

  const { data: assignmentsData } = useListCustomBenefitAssignments(benefitId, assignmentsStart);
  const { mutateAsync: createAssignments } = useCreateCustomBenefitAssignments(benefitId);
  const { mutateAsync: deleteAssignments } = useDeleteCustomBenefitAssignments(benefitId);

  const hasPendingAssignments =
    assignmentsData?.pages
      .flatMap((page) => page.results)
      .some((employee) => employee.pending_assignments.length > 0) ?? false;

  const benefitMap: Partial<Record<BenefitEnum, string>> = {
    sachbezug: t('benefits:sachbezug'),
    betreuungskosten: t('benefits:betreuungskosten'),
    telefonkosten: t('benefits:telefonkosten'),
    fahrtkosten: t('benefits:fahrtkosten'),
    internetpauschale: t('benefits:internetpauschale'),
    öpnv_tickets: t('benefits:öpnv_tickets'),
    essenszuschuss: t('benefits:essenszuschuss')
  };

  const pendingAssignmentsConflictFree = async () => {
    if (newCustomBenefit.editingBenefitId) {
      const result = await companyStore.fetchCustomBenefitAssignments(
        newCustomBenefit.editingBenefitId,
        newCustomBenefit.employeeAssignmentsStart
          ? newCustomBenefit.employeeAssignmentsStart.getMonth() + 1
          : undefined,
        newCustomBenefit.employeeAssignmentsStart
          ? newCustomBenefit.employeeAssignmentsStart.getFullYear()
          : undefined
      );
      return !!(
        result &&
        !result.results?.some((employee) =>
          employee.pending_assignments.some((assignment) =>
            (assignment?.assignment_type ?? '').includes('invalid')
          )
        )
      );
    }
    return Promise.resolve(false);
  };

  const handleApply = async () => {
    setIsValidatingConstraints(true);
    try {
      if (newCustomBenefit.editingBenefitId) {
        newCustomBenefit.applyPendingAssignments = true;
        newCustomBenefit.correctPendingAssignments = correctAssignments;
        await patch(newCustomBenefit.editingBenefitId);
        snackbar.enqueueSnackbar({
          message: t('custom:changes_applied'),
          details: t('custom:changes_application_hint', {
            start: newCustomBenefit.employeeAssignmentsStart?.toLocaleString('default', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })
          }),
          variant: 'detailedSuccess'
        });
        newCustomBenefit.clear();
        queryClient.removeQueries({
          queryKey: customBenefitAssignmentKeys.all(benefitId)
        });
        navigate('/benefits/custom');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      let errorMessage = t('custom:error');

      if (e instanceof ApiError) {
        if (e.body && typeof e.body === 'object') {
          const errorMessages: string[] = [];
          Object.keys(e.body).forEach((key) => {
            errorMessages.push(e.body[key]);
          });
          errorMessage = '\n' + errorMessages.join(', ');
        }
      }
      if (errorMessage) {
        if (
          errorMessage.includes('Invalid individual assignment') ||
          errorMessage.includes('Invalid global assignment')
        ) {
          setCorrectAssignments(true);
        } else {
          snackbar.enqueueSnackbar({
            message: errorMessage,
            variant: 'error'
          });
        }
      }
    }
    setIsValidatingConstraints(false);
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Guard the entry into this page: without a benefit in context there is
  // nothing to assign. The guard must only fire on arrival — the leaving
  // handlers clear the benefit and navigate themselves, and re-checking after
  // that clear would fire a second, competing navigation.
  const entryCheckedRef = useRef(false);
  useEffect(() => {
    if (entryCheckedRef.current) return;
    entryCheckedRef.current = true;
    if (!newCustomBenefit.name_de) {
      navigate('/benefits/custom');
    }
  }, [navigate, newCustomBenefit.name_de]);

  useEffect(() => {
    return () => {
      newCustomBenefit.clear();
      queryClient.removeQueries({
        queryKey: customBenefitAssignmentKeys.all(benefitId)
      });
    };
  }, [benefitId, newCustomBenefit, queryClient]);

  const getConfirmationContentText = () => {
    if (correctAssignments) {
      return t('custom:apply_changes_outdated_assignments', {
        benefit: benefitMap[newCustomBenefit.influencedBenefit as BenefitEnum]
      });
    }
    const date = (newCustomBenefit.employeeAssignmentsStart ?? newCustomBenefit.companyStart)
      .toLocaleString('default', { day: 'numeric', month: 'long', year: 'numeric' })
      .replace(' ', '\u00A0');
    return conflictFree
      ? t('custom:apply_changes_hint', { month: date })
      : t('custom:apply_changes_conflicts_hint');
  };

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '82vh',
        padding: '1.5rem 2rem'
      }}
    >
      <Grid container alignItems="center" justifyContent="space-between">
        <div>
          <Typography className="text-lg font-bold">{newCustomBenefit.name_de}</Typography>
          <Typography className="text-sm text-grey-600">{t('custom:assignment_button')}</Typography>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            marginBottom: '8px',
            marginTop: '-10px',
            justifyContent: 'flex-end'
          }}
        >
          <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', marginLeft: '5px' }}>
            <Button
              variant="secondary"
              onClick={async () => {
                if (hasPendingAssignments) {
                  setShowLeavingDialog(true);
                } else {
                  try {
                    await deleteAssignments({ assignmentIds: [], all: true });
                  } catch {
                    // Ignore errors when discarding — we're navigating away
                  }
                  newCustomBenefit.clear();
                  queryClient.removeQueries({
                    queryKey: customBenefitAssignmentKeys.all(benefitId)
                  });
                  navigate('/benefits/custom');
                }
              }}
            >
              {t('common:BACK')}
            </Button>
            <Button
              onClick={async () => {
                setIsValidatingConstraints(true);
                const isConflictFree = await pendingAssignmentsConflictFree();
                setConflictFree(isConflictFree);
                setIsValidatingConstraints(false);
                setShowApplyDialog(true);
              }}
            >
              <SaveIcon />
              {t('custom:apply')}
            </Button>
          </div>
        </div>
        <Grid item xs={12}>
          <Divider
            style={{
              marginTop: '12px',
              marginBottom: '12px',
              marginRight: '-50px',
              marginLeft: '-50px',
              backgroundColor: 'grey.50',
              height: '0.2px'
            }}
          />
        </Grid>
        <Grid container alignItems="center" marginTop={-1}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '3rem',
              flexWrap: 'wrap',
              width: '100%'
            }}
          >
            <div
              style={{
                flex: 1,
                marginTop: '1rem',
                marginBottom: '1rem',
                color: 'grey.50'
              }}
            >
              <Box
                sx={{
                  borderRadius: '15px',
                  backgroundColor: 'grey.50',
                  fontFamily: 'Rubik',
                  padding: '12px',
                  width: '100%'
                }}
              >
                <Typography
                  noWrap
                  style={{
                    fontSize: '13px',
                    color: theme.palette.grey['500']
                  }}
                >
                  {t('custom:influence_on')}
                </Typography>
                <Typography
                  noWrap
                  style={{
                    marginTop: '-4px',
                    fontWeight: 'bolder'
                  }}
                >
                  {newCustomBenefit.constraintType && newCustomBenefit.influencedBenefit
                    ? newCustomBenefit.constraintType === 'replace'
                      ? t('custom:replaces', {
                          benefit: benefitMap[newCustomBenefit.influencedBenefit as BenefitEnum]
                        })
                      : t('custom:limits', {
                          benefit: benefitMap[newCustomBenefit.influencedBenefit as BenefitEnum]
                        })
                    : t('custom:no_influence')}
                </Typography>
              </Box>
            </div>
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                marginTop: '1rem',
                marginBottom: '1rem',
                color: theme.palette.primary.main
              }}
            >
              <Box
                sx={{
                  borderRadius: '15px',
                  backgroundColor: 'grey.50',
                  fontFamily: 'Rubik',
                  padding: '12px',
                  width: '100%'
                }}
              >
                <Typography
                  noWrap
                  style={{
                    fontSize: '13px',
                    color: theme.palette.grey['500']
                  }}
                >
                  {t('custom:company_amount')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '-4px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>
                    <b>{Util.formatCurrency(newCustomBenefit.companyAmount)}</b>{' '}
                    <span style={{ marginLeft: '8px', fontSize: '16px' }}>{t('common:SINCE')}</span>{' '}
                    {newCustomBenefit.companyStart.toLocaleString('default', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                  <LockIcon width={'15px'} color={'night'} />
                </Box>
              </Box>
            </div>
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                marginTop: '1rem',
                marginBottom: '1rem',
                color: theme.palette.primary.main
              }}
            >
              <Box
                sx={{
                  borderRadius: '15px',
                  backgroundColor: 'grey.50',
                  fontFamily: 'Rubik',
                  padding: '12px',
                  width: '100%'
                }}
              >
                <Typography
                  noWrap
                  style={{
                    marginRight: '2rem',
                    fontSize: '13px',
                    color: theme.palette.grey['500']
                  }}
                >
                  {t('custom:assignments_valid_from')}
                </Typography>
                <Typography
                  noWrap
                  style={{
                    marginTop: '-4px',
                    fontWeight: 'bolder',
                    color: theme.palette.primary.main
                  }}
                >
                  {(
                    newCustomBenefit.employeeAssignmentsStart ?? newCustomBenefit.companyStart
                  ).toLocaleString('default', { month: 'long', year: 'numeric' })}
                </Typography>
              </Box>
            </div>
            <div
              style={{
                marginTop: '1.75rem'
              }}
            >
              <IconButton onClick={() => setShowInfoDrawer(true)}>
                <HelpIcon />
              </IconButton>
            </div>
          </div>
        </Grid>
      </Grid>
      <div style={{ flex: 1, minHeight: 0 }}>
        <CustomBenefitAssignmentTable
          benefitId={benefitId}
          benefitName={newCustomBenefit.name_de || ''}
          companyAmount={newCustomBenefit.companyAmount ?? 0}
          assignmentsStart={assignmentsStart}
          onBulkAssign={(employeeIds) => {
            setSelectedEmployeeIds(employeeIds);
            setShowAssignmentDialog(true);
          }}
        />
      </div>
      <CurrencyInputDialog
        open={showAssignmentDialog}
        onClose={() => setShowAssignmentDialog(false)}
        onCancel={() => setShowAssignmentDialog(false)}
        title={t('custom:assign_individual_budget')}
        contentText={t('custom:assign_individual_budget_hint', {
          count: selectedEmployeeIds.length,
          customBenefit: newCustomBenefit.name_de
        })}
        buttonDisabled={isValidatingConstraints}
        onConfirm={async (bulkUpdateAmount) => {
          if (newCustomBenefit.editingBenefitId && newCustomBenefit.employeeAssignmentsStart) {
            setIsValidatingConstraints(true);
            await createAssignments({
              startAccountingPeriod: assignmentsStart,
              amount: bulkUpdateAmount,
              employees: selectedEmployeeIds
            });
          }
          setShowAssignmentDialog(false);
          setIsValidatingConstraints(false);
        }}
      />
      <ConfirmationDialog
        open={showLeavingDialog}
        title={t('custom:save_changes_as_draft')}
        contentText={t('custom:save_changes_as_draft_hint')}
        onConfirm={() => {
          // Only navigate here. React Router v7 commits the location change in
          // a startTransition, and any urgent update dispatched in the same
          // event handler (closing the dialog, clearing the MobX store) preempts
          // that transition, so the router rolls the URL back to this page and
          // the redirect silently fails. Store reset and query cleanup happen in
          // the unmount effect once the navigation has committed.
          navigate('/benefits/custom');
        }}
        onCancel={async () => {
          try {
            await deleteAssignments({ assignmentIds: [], all: true });
          } catch {
            // Ignore errors when discarding — we're navigating away
          }
          newCustomBenefit.clear();
          queryClient.removeQueries({
            queryKey: customBenefitAssignmentKeys.all(benefitId)
          });
          navigate('/benefits/custom');
        }}
        onClose={() => setShowLeavingDialog(false)}
        confirmButtonText={t('common:SAVE')}
        cancelButtonText={t('common:DISCARD')}
        variant="question"
      />
      <ConfirmationDialog
        open={showApplyDialog}
        title={correctAssignments ? t('custom:correct_assignments') : t('custom:apply_changes')}
        contentText={getConfirmationContentText()}
        onConfirm={async () => {
          await handleApply();
        }}
        onCancel={() => {
          setCorrectAssignments(false);
          setShowApplyDialog(false);
        }}
        onClose={() => setShowApplyDialog(false)}
        confirmButtonText={t('common:CONFIRM')}
        cancelButtonText={t('common:CANCEL')}
        hideCancelButton={false}
        variant={correctAssignments ? 'error' : 'question'}
      />
      <Drawer
        label={t('custom:drawer_budget_headline')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <div>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_1_text')}
          </Typography>
          <Typography style={{ marginTop: '1.3rem', fontWeight: 'bold', color: 'inherit' }}>
            {t('custom:info_paragraph_2_headline')}
          </Typography>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_2_text')}
          </Typography>
          <Typography
            style={{
              marginTop: '0.25rem',
              fontWeight: '600',
              fontSize: 'smaller',
              lineHeight: '1.5'
            }}
          >
            {t('custom:info_paragraph_2_subheadline_1')}
          </Typography>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_2_sub_1_text')}
          </Typography>
          <Typography
            style={{
              marginTop: '0.5rem',
              fontWeight: '600',
              color: 'inherit',
              fontSize: 'smaller',
              lineHeight: '1.5'
            }}
          >
            {t('custom:info_paragraph_2_subheadline_2')}
          </Typography>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_2_sub_2_text')}
          </Typography>
          <Typography style={{ marginTop: '1.3rem', fontWeight: 'bold', color: 'inherit' }}>
            {t('custom:info_paragraph_3_headline')}
          </Typography>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_3_text')}
          </Typography>
          <Typography
            style={{
              marginTop: '0.5rem',
              fontWeight: '600',
              color: 'inherit',
              fontSize: 'smaller',
              lineHeight: '1.5'
            }}
          >
            {t('custom:info_paragraph_3_subheadline_2')}
          </Typography>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_3_sub_2_text')}
          </Typography>
          <Typography style={{ marginTop: '1.3rem', fontWeight: 'bold', color: 'inherit' }}>
            {t('custom:info_paragraph_4_headline')}
          </Typography>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_4_text')}
          </Typography>
          <Typography
            style={{
              marginTop: '0.5rem',
              fontWeight: '600',
              color: 'inherit',
              fontSize: 'smaller',
              lineHeight: '1.5'
            }}
          >
            {t('custom:info_paragraph_4_subheadline_1')}
          </Typography>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_4_sub_1_text_1')}
          </Typography>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_4_sub_1_text_2')}
          </Typography>
          <Typography
            style={{
              marginTop: '0.5rem',
              fontWeight: '600',
              color: 'inherit',
              fontSize: 'smaller',
              lineHeight: '1.5'
            }}
          >
            {t('custom:info_paragraph_4_subheadline_2')}
          </Typography>
          <Typography style={{ fontSize: 'smaller', lineHeight: '1.5' }}>
            {t('custom:info_paragraph_4_sub_2_text')}
          </Typography>
        </div>
      </Drawer>
      {showAutomaticAssignmentDialog && (
        <AutomaticAssignmentDialog
          title={t('custom:automatic_assignment')}
          confirmButtonText={t('common:CONFIRM')}
          cancelButtonText={t('common:CANCEL')}
          onClose={() => {
            setShowAutomaticAssignmentDialog(false);
          }}
          open={showAutomaticAssignmentDialog}
          onConfirm={async ([companyAmount, startMonth, startYear]) => {
            newCustomBenefit.companyAmount = companyAmount;
            newCustomBenefit.companyStart = new Date(startYear, startMonth, 1);
            if (newCustomBenefit.editingBenefitId) {
              setIsValidatingConstraints(true);
              await patch(newCustomBenefit.editingBenefitId);
              queryClient.invalidateQueries({
                queryKey: customBenefitAssignmentKeys.all(benefitId)
              });
            }
            setIsValidatingConstraints(false);
            setShowAutomaticAssignmentDialog(false);
          }}
          amount={newCustomBenefit.companyAmount}
          monthDeactivated={true}
          month={newCustomBenefit.companyStart}
        />
      )}
      {isValidatingConstraints && (
        <LoadingDialog text={t('custom:validating_assignments')} open={isValidatingConstraints} />
      )}
    </Paper>
  );
};
